import { AnimatePresence, motion } from "framer-motion";
import React, { FC, useCallback, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { useLabels } from "../../../contexts/labels";
import { MaterialType } from "../../../types/materials";
import { renameFields } from "../../../utils/object";
import { Select } from "../../forms";
import { otherMaterial } from "../../forms/forms-fields/materials-form-fields/constants";
import { CustomerStoriesFilter } from "./customer-stories-filter";
import { CustomerStoriesGrid, CustomerStoriesGridProps } from "./customer-stories-grid";
import { orderByType, sortStories } from "./customer-stories-order-by";
import { FormPayload } from "./types";

export interface CustomerStoriesGridSectionProps {
  items: Queries.CustomerStoryCardDataFragment[];
}

export const CustomerStoriesGridSection: FC<CustomerStoriesGridSectionProps> = ({ items }) => {
  const { labels: stringLabels } = useLabels();
  const labels = renameFields(stringLabels, {
    materials: "forms-materials",
    [MaterialType.Wood]: "materials-wood",
    [MaterialType.Stone]: "materials-stone",
    [MaterialType.Glass]: "materials-glass",
    [MaterialType.Materia]: "materials-materia",
    [MaterialType.Metal]: "materials-metal",
    [otherMaterial]: "materials-other",
  });

  const stories = items //
    .reduce<CustomerStoriesGridProps["stories"]>((acc, story) => {
      const arrayToAdd = Array.from(story?.standardpage ?? [], (page) => ({
        pageId: page?.id,
        ...(story as Omit<Queries.CustomerStoryCardDataFragment, "standardpage">),
      }));

      return [...acc, ...arrayToAdd];
    }, []);

  const defaultFormValue = {
    nameSearch: "",
    descriptionSearch: "",
    materials: {
      [MaterialType.Glass]: false,
      [MaterialType.Materia]: false,
      [MaterialType.Stone]: false,
      [MaterialType.Wood]: false,
      [MaterialType.Metal]: false,
      [otherMaterial]: false,
    },
  };

  const formMethods = useForm<FormPayload>({
    defaultValues: defaultFormValue,
  });
  const [orderBy, setOrderBy] = useState(orderByType.NewerToOlder);

  const { watch } = formMethods;
  const [submittedFormPayload, setSubmittedFormPayload] = useState<FormPayload>(defaultFormValue);
  const [animationCounter, setAnimationCounter] = useState(0);

  const handleSubmitPayload = useCallback(() => {
    setSubmittedFormPayload(watch());
    setAnimationCounter((x) => x++);
  }, [setSubmittedFormPayload, setAnimationCounter, watch]);

  const storiesToShow = useMemo(() => {
    const { materials, nameSearch, descriptionSearch } = submittedFormPayload;

    const filteredStories = stories.filter((story) => {
      const isSomeMaterialFiltered = Object.values(materials).some(Boolean);
      const materialMismatch =
        !story?.specialization?.machiningType?.material ||
        !materials[story.specialization.machiningType.material as MaterialType];
      if (isSomeMaterialFiltered && materialMismatch) {
        return false;
      }

      if (nameSearch && !story?.name?.toLowerCase()?.includes(nameSearch.toLowerCase())) {
        return false;
      }

      if (
        descriptionSearch &&
        !story?.description?.raw?.toLowerCase()?.includes(descriptionSearch.toLowerCase())
      ) {
        return false;
      }
      return true;
    });

    const sortedStories = sortStories(filteredStories, orderBy ?? orderByType.NewerToOlder);

    return sortedStories;
  }, [submittedFormPayload, orderBy, stories]);

  return (
    <div className="w-full flex justify-center py-16 px-8 lg:px-20">
      <div className="flex flex-col gap-10 w-full bg-neutral">
        <div className="w-full relative">
          <FormProvider {...formMethods}>
            <CustomerStoriesFilter
              onSubmit={handleSubmitPayload}
              labels={labels}
              submittedFormPayload={submittedFormPayload}
            />
          </FormProvider>
          <Select
            className="col-span-12 lg:col-span-6 absolute top-0 right-0 z-10"
            onChange={(e) => setOrderBy(e.target.value as orderByType)}
          >
            <option value={orderByType.NewerToOlder} disabled>
              {labels["customer-stories-order-by"]}
            </option>
            {Object.values(orderByType).map((orderOption, i) => (
              <option key={i} value={orderOption}>
                {labels[`customer-stories-order-by-${orderOption}`]}
              </option>
            ))}
          </Select>
        </div>
        <AnimatePresence initial={false} mode="popLayout">
          <motion.div
            className="h-full flex w-full flex-col justify-stretch"
            // This key is needed to rerender the component with animation any time a filter parameter is set
            key={orderBy + animationCounter}
            initial={{
              opacity: 0,
            }}
            animate={{
              opacity: 1,
            }}
            exit={{
              opacity: 0,
            }}
            transition={{
              opacity: { ease: "easeOut", duration: 0.5 },
            }}
          >
            <CustomerStoriesGrid labels={labels} stories={storiesToShow} />
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};
